import React, { Fragment, useCallback, useRef, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { forOwn, get, has } from 'lodash-es';
import Form from 'reactstrap/lib/Form';
import { animateScroll } from 'react-scroll';

/* MODELS */
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import { ConsumerReviewsModel, ConsumerReviewsPaths } from 'client/data/models/consumer-reviews';
import { VisitorEntities, VisitorModel } from 'client/data/models/visitor';

/* TRACKING */
import { TrackingHandler } from 'client/tracking/handler';
import { EventToolbox } from 'client/utils/event-toolbox';

/* SHARED COMPONENTS */
import { formValidation } from 'site-modules/shared/components/form-validation/form-validation';
import { insiderMethods } from 'site-modules/shared/components/profile/insider-methods';

/* COMPONENTS */
import { VehicleReviewAndDetails } from 'site-modules/write-review/components/steps/vehicle-review-and-details/vehicle-review-and-details';
import { AuthorInfo } from 'site-modules/write-review/components/steps/author-info/author-info';
import { MoreRatingsAndAuthorInfo } from 'site-modules/write-review/components/steps/more-ratings-and-author-info/more-ratings-and-author-info';
import { MoreRatingsAndAuthorInfoConfirmed } from 'site-modules/write-review/components/steps/more-ratings-and-author-info-confirmed/more-ratings-and-author-info-confirmed';
import { ThankYouPage } from 'site-modules/write-review/components/steps/thank-you-page/thank-you-page';
import { TryAgain } from 'site-modules/write-review/components/steps/try-again/try-again';

/* UTILS */
import { venomHistory } from 'client/utils/history/venom-history';
import { isUnknownZip } from 'site-modules/shared/utils/location';
import { getQuery } from 'client/utils/location';
import { isUsed } from 'site-modules/shared/utils/publication-states';
import { saveReviewInfoToIdm } from 'site-modules/write-review/utils/insider';
import { signOut } from 'site-modules/shared/components/profile/firebase-auth';

/* CONSTANTS */
import { PUB_STATES } from 'client/constants/pub-states';
import { FORM_VALIDATORS, STEP_NUMBER, SUBMIT_CTA } from 'site-modules/shared/constants/write-review/write-review';

import './write-review-form.scss';

const SCROLL_DURATION = 300;
const THANK_YOU_PAGE_QUERY = 'thank-you';

export function WriteReviewFormUI({
  reviewId = null,
  fields,
  validate,
  resetFields,
  fieldRef,
  validationErrors = {},
  vehicle = null,
  location,
  visitorLocation = null,
  createAnonymousIdmProfile,
  profileDisplayName = null,
  profileEmail = null,
  validateEmailAsync,
  updateIdmData,
  setModelValue,
  isAnonymousUser = null,
  isAuthenticated = null,
  isConfirmed = false,
}) {
  const { pathname, search } = location;
  const query = getQuery(location);
  const isThankYouPage = has(query, THANK_YOU_PAGE_QUERY);
  const initialStep = isConfirmed ? STEP_NUMBER.STEP_4 : STEP_NUMBER.STEP_1;
  const [step, setStep] = useState(isThankYouPage ? STEP_NUMBER.STEP_5 : initialStep);
  const [isLoading, setLoading] = useState(false);
  const [previousStep, setPreviousStep] = useState(null);
  const reviewInfo = useRef({
    make: get(vehicle, 'make.slug'),
    model: get(vehicle, 'model.slug'),
    year: get(vehicle, 'year'),
  });

  const goNextStep = useCallback(
    nextStep => {
      animateScroll.scrollToTop({ duration: SCROLL_DURATION });

      const newStep = nextStep || step + 1;

      setPreviousStep(step);
      setStep(newStep);
      setLoading(false);
      EventToolbox.fireTrackAction({
        event_type: 'page_enter',
        event_data: {
          action_cause: 'page_load',
        },
      });

      if (newStep === STEP_NUMBER.STEP_5) {
        venomHistory.push(
          `${pathname}${search}${classnames({ '&': search.length, '?': !search.length })}${THANK_YOU_PAGE_QUERY}`
        );
      }
    },
    [pathname, search, step]
  );

  const componentFieldRef = field => {
    if (!field) {
      return undefined;
    }
    return fieldRef(field.props.name, field);
  };

  const onUserChange = useCallback(() => {
    signOut();
    forOwn(fields, ({ reset }) => {
      reset();
    });
  }, [fields]);

  const saveReviewInfo = useCallback(() => {
    forOwn(fields, ({ value }, name) => {
      reviewInfo.current[name] = value();
    });
  }, [fields]);

  const submitReview = useCallback(
    () =>
      setModelValue(ConsumerReviewsPaths.getCreateAndUpdateReviewIdPath(), ConsumerReviewsModel, {
        ...reviewInfo.current,
        ...(isUnknownZip(visitorLocation)
          ? {}
          : { city: get(visitorLocation, 'city'), state: get(visitorLocation, 'stateCode') }),
        reviewId,
      }),
    [reviewId, setModelValue, visitorLocation]
  );

  const onStepSubmit = useCallback(async () => {
    switch (step) {
      case STEP_NUMBER.STEP_1: {
        saveReviewInfo();
        resetFields();
        goNextStep();
        if (!isAuthenticated) {
          TrackingHandler.fireEvent('wr_begin_sign_up');
        }
        break;
      }
      case STEP_NUMBER.STEP_2: {
        saveReviewInfo();
        setLoading(true);

        // In case user enters email via input
        if (!(isAuthenticated && profileEmail && profileDisplayName)) {
          const { isEmailValid } = await validateEmailAsync();
          if (!isEmailValid) {
            setLoading(false);
            break;
          }
        }

        return submitReview().catch(() => {
          goNextStep(STEP_NUMBER.STEP_TRY_AGAIN);
        });
      }
      case STEP_NUMBER.STEP_3: {
        saveReviewInfo();
        setLoading(true);
        return submitReview()
          .then(() => {
            goNextStep(STEP_NUMBER.STEP_5);
          })
          .catch(() => {
            goNextStep(STEP_NUMBER.STEP_TRY_AGAIN);
          });
      }
      case STEP_NUMBER.STEP_4: {
        saveReviewInfo();
        setLoading(true);
        return submitReview()
          .then(() => goNextStep())
          .catch(() => {
            goNextStep(STEP_NUMBER.STEP_TRY_AGAIN);
          });
      }
      case STEP_NUMBER.STEP_TRY_AGAIN: {
        goNextStep(previousStep);
        break;
      }
      default: {
        break;
      }
    }

    return Promise.resolve();
  }, [
    goNextStep,
    isAuthenticated,
    previousStep,
    profileDisplayName,
    profileEmail,
    resetFields,
    saveReviewInfo,
    step,
    submitReview,
    validateEmailAsync,
  ]);

  useEffect(() => {
    if (!reviewId || step !== STEP_NUMBER.STEP_2) {
      return;
    }

    saveReviewInfoToIdm({
      reviewInfo: {
        ...reviewInfo.current,
        reviewId,
        make: get(vehicle, 'make.name'),
        model: get(vehicle, 'model.name'),
      },
      vehicleData: {
        modelLinkCode: get(vehicle, 'model.modelLinkCode'),
        type: isUsed(get(vehicle, 'pubStates')) ? PUB_STATES.USED : PUB_STATES.NEW,
      },
      createAnonymousIdmProfile,
      updateIdmData,
      isAuthenticated,
      isAnonymousUser,
    })
      .then(() => {
        TrackingHandler.fireEvent('wr_save_vehicle_review', { value: SUBMIT_CTA });
        resetFields();
        reviewInfo.current = {};
        goNextStep();
      })
      .catch(() => {
        goNextStep(STEP_NUMBER.STEP_TRY_AGAIN);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewId, step]);

  const isTryAgain = step === STEP_NUMBER.STEP_TRY_AGAIN;
  const stepToRender = isTryAgain ? previousStep : step;
  const stepProps = {
    vehicle,
    validate,
    validationErrors,
    fieldRef: componentFieldRef,
    onSubmit: onStepSubmit,
    isLoading,
  };
  const profileData = {
    email: profileEmail,
    displayName: profileDisplayName,
    isAuthenticated,
  };

  return (
    <Fragment>
      <Form id="write-review-form" className={classnames('write-review-form', { 'd-none': isTryAgain })}>
        {stepToRender === STEP_NUMBER.STEP_1 && <VehicleReviewAndDetails {...stepProps} />}
        {stepToRender === STEP_NUMBER.STEP_2 && (
          <AuthorInfo {...stepProps} profileData={profileData} onUserChange={onUserChange} />
        )}
        {stepToRender === STEP_NUMBER.STEP_3 && <MoreRatingsAndAuthorInfo {...stepProps} />}
        {stepToRender === STEP_NUMBER.STEP_4 && <MoreRatingsAndAuthorInfoConfirmed {...stepProps} />}
        {stepToRender === STEP_NUMBER.STEP_5 && <ThankYouPage vehicle={vehicle} />}
      </Form>
      {isTryAgain && <TryAgain onSubmit={onStepSubmit} />}
    </Fragment>
  );
}

WriteReviewFormUI.propTypes = {
  vehicle: VehicleEntities.MakeModelSubmodelYear,
  fields: PropTypes.shape({}).isRequired,
  fieldRef: PropTypes.func.isRequired,
  validate: PropTypes.func.isRequired,
  validateEmailAsync: PropTypes.func.isRequired,
  resetFields: PropTypes.func.isRequired,
  setModelValue: PropTypes.func.isRequired,
  updateIdmData: PropTypes.func.isRequired,
  createAnonymousIdmProfile: PropTypes.func.isRequired,
  profileEmail: PropTypes.string,
  profileDisplayName: PropTypes.string,
  isAuthenticated: PropTypes.bool,
  isAnonymousUser: PropTypes.bool,
  reviewId: PropTypes.string,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }).isRequired,
  visitorLocation: VisitorEntities.Location,
  isConfirmed: PropTypes.bool,
  validationErrors: PropTypes.shape({}),
};

const mapStateToProps = state => ({
  profileEmail: get(state, 'profile.data.idm.identifiers.email[0]'),
  profileDisplayName: get(state, 'profile.data.idm.edmundsMembership.userName'),
  isAuthenticated: get(state, 'profile.isAuthenticated'),
  isAnonymousUser: get(state, 'profile.isAnonymousUser'),
});

export const WriteReviewForm = connect(mapStateToProps)(
  connectToModel(formValidation(insiderMethods(WriteReviewFormUI), FORM_VALIDATORS), {
    visitorLocation: bindToPath('location', VisitorModel),
    reviewId: bindToPath(ConsumerReviewsPaths.getCreateAndUpdateReviewIdPath, ConsumerReviewsModel),
  })
);
